import {
  analyzeTextForComparison,
  FOLIATION_OR_PAGE_PATTERNS,
  STANDALONE_NUMERIC_LINE
} from './comparisonTextCleaner'
import {
  cleanSourceTextForModel,
  type SourceCleaningReport
} from './sourceTextCleaner'
import type { CompleteMicroSegmentationResult } from '../types/segmentationTypes'
import {
  HAND_CHANGE_TAG_REGEX_SOURCE,
  LEGEND_EXPLICIT_BLOCK_REGEX_SOURCE,
  LEGEND_EXPLICIT_BOUNDARY_TAG_REGEX_SOURCE,
} from './editorialMarkerGrammar'

export interface LedgerTextArtifactSummary {
  foliationMarkerCount: number
  pageMarkerCount: number
  standaloneLineNumberCount: number
  pipelineMarkerCount: number
  illustrationCount: number
  legendCount: number
  sideTitleCount: number
  apparatusBlockCount: number
  footnoteAnchorCount: number
}

export interface LedgerTextPreprocessingResult {
  normalizedSourceText: string
  cleanSourceText: string
  workspaceDisplayText: string
  readingText: string
  comparisonText: string
  artifactSummary: LedgerTextArtifactSummary
  sourceCleaningReport: SourceCleaningReport
}

export interface WitnessUnitTextRecord {
  rawText: string
  cleanSourceText: string
  workspaceDisplayText: string
  readingText: string
  comparisonText: string
}

interface WitnessTextSource {
  uploaded_file?: {
    content?: string
  }
  segmentation_result?: CompleteMicroSegmentationResult | null
  segmentation_runs?: Record<string, { result?: CompleteMicroSegmentationResult | null }>
  active_segmentation_run?: string
}

const MAX_PREPROCESS_CACHE_ENTRIES = 4000
const preprocessCache = new Map<string, LedgerTextPreprocessingResult>()

const PAGE_OR_PDF_LINE = /^\s*(?:p(?:age)?\.?\s*)?[0-9\u0660-\u0669\u06F0-\u06F9]+\s*$|^\s*pdf\s*[0-9\u0660-\u0669\u06F0-\u06F9]+\s*$/i
const INLINE_BRACKETED_FOLIATION = /\[(?:\*?\d+\s*[rv]\*?|\*\d+\s*[rv]|\d+\s*[rv]\*?)\]/gi
const LO_MARKER = /\/?Lo[–-]\d+(?:[.\-–]+\d+)*(?:\/)?/gi
const ILLUSTRATION_MARKER = /\[(?:illustration|miniature)\]/gi
const LEGEND_TAG_BLOCK = /<legend>([\s\S]*?)<\/legend>/gi
const LEGEND_BRACKET_BLOCK = /\[legend\]([\s\S]*?)\[legend\]/gi
const LEGEND_EXPLICIT_BLOCK = new RegExp(LEGEND_EXPLICIT_BLOCK_REGEX_SOURCE, 'gi')
const LEGEND_EXPLICIT_BOUNDARY_TAG = new RegExp(LEGEND_EXPLICIT_BOUNDARY_TAG_REGEX_SOURCE, 'gi')
const HAND_CHANGE_TAG = new RegExp(HAND_CHANGE_TAG_REGEX_SOURCE, 'gi')
const SIDE_TITLE_BLOCK = /\[side\s+title,\s*([^\]]+)\]/gi
const FOOTNOTE_DIGIT_ANCHOR = /(\S)[*†‡§]?[0-9\u0660-\u0669\u06F0-\u06F9]+(?=(?:\s|$))/g
const APPARATUS_DIVIDER_LINE = /^\s*[-–—_]{3,}\s*$/
const APPARATUS_NOTE_LINE = /^\s*(?:[*†‡§]?\s*[0-9\u0660-\u0669\u06F0-\u06F9]+[\]).:-]?\s+.+|note\b.+|apparatus\b.+|ملحوظة.+|هامش.+)$/i

const collapseWhitespace = (value: string): string => value.replace(/\s+/g, ' ').trim()

const normalizeLineEndings = (value: string): string => value.replace(/\r\n?/g, '\n')

const countMatches = (value: string, pattern: RegExp): number => {
  const matcher = new RegExp(pattern.source, pattern.flags.includes('g') ? pattern.flags : `${pattern.flags}g`)
  return (value.match(matcher) || []).length
}

const removeApparatusBlocks = (value: string): { text: string; count: number } => {
  const lines = normalizeLineEndings(value).split('\n')
  const keptLines: string[] = []
  let removedCount = 0
  let insideApparatus = false

  lines.forEach(line => {
    const trimmed = line.trim()

    if (APPARATUS_DIVIDER_LINE.test(trimmed)) {
      removedCount += 1
      insideApparatus = true
      return
    }

    if (insideApparatus) {
      if (!trimmed) {
        return
      }
      if (APPARATUS_NOTE_LINE.test(trimmed)) {
        return
      }
      insideApparatus = false
    }

    keptLines.push(line)
  })

  return {
    text: keptLines.join('\n'),
    count: removedCount
  }
}

const applySharedNoiseRemoval = (
  value: string
): {
  text: string
  summary: LedgerTextArtifactSummary
} => {
  const summary: LedgerTextArtifactSummary = {
    foliationMarkerCount: countMatches(value, INLINE_BRACKETED_FOLIATION) + FOLIATION_OR_PAGE_PATTERNS.reduce((total, pattern) => (
      total + countMatches(value, pattern)
    ), 0),
    pageMarkerCount: value
      .split('\n')
      .filter(line => PAGE_OR_PDF_LINE.test(line.trim()))
      .length,
    standaloneLineNumberCount: (value.match(STANDALONE_NUMERIC_LINE) || []).length,
    pipelineMarkerCount: countMatches(value, LO_MARKER),
    illustrationCount: countMatches(value, ILLUSTRATION_MARKER),
    legendCount: countMatches(value, LEGEND_TAG_BLOCK)
      + countMatches(value, LEGEND_BRACKET_BLOCK)
      + countMatches(value, LEGEND_EXPLICIT_BLOCK),
    sideTitleCount: countMatches(value, SIDE_TITLE_BLOCK),
    apparatusBlockCount: 0,
    footnoteAnchorCount: countMatches(value, FOOTNOTE_DIGIT_ANCHOR)
  }

  const apparatusStripped = removeApparatusBlocks(value)
  summary.apparatusBlockCount = apparatusStripped.count

  let text = apparatusStripped.text
  text = text.replace(INLINE_BRACKETED_FOLIATION, ' ')
  FOLIATION_OR_PAGE_PATTERNS.forEach(pattern => {
    text = text.replace(pattern, ' ')
  })
  text = text.replace(LO_MARKER, ' ')
  text = text.replace(STANDALONE_NUMERIC_LINE, '\n')
  text = text
    .split('\n')
    .filter(line => !PAGE_OR_PDF_LINE.test(line.trim()))
    .join('\n')
  text = text.replace(FOOTNOTE_DIGIT_ANCHOR, '$1')

  return { text, summary }
}

const setCachedPreprocessingResult = (key: string, value: LedgerTextPreprocessingResult): void => {
  preprocessCache.set(key, value)
  if (preprocessCache.size <= MAX_PREPROCESS_CACHE_ENTRIES) {
    return
  }
  const firstKey = preprocessCache.keys().next().value
  if (typeof firstKey === 'string') {
    preprocessCache.delete(firstKey)
  }
}

const resolveActiveSegmentationResult = (
  witness: WitnessTextSource | null | undefined
): CompleteMicroSegmentationResult | null => {
  if (!witness) {
    return null
  }
  const activeRunId = witness.active_segmentation_run
  if (activeRunId && witness.segmentation_runs?.[activeRunId]?.result) {
    return witness.segmentation_runs[activeRunId]?.result || null
  }
  return witness.segmentation_result || null
}

export const preprocessLedgerText = (rawText: string): LedgerTextPreprocessingResult => {
  const cacheKey = rawText || ''
  const cached = preprocessCache.get(cacheKey)
  if (cached) {
    return cached
  }

  const normalizedSourceText = normalizeLineEndings(rawText || '')
  const sourceCleaning = cleanSourceTextForModel(normalizedSourceText)
  const { text: sharedNoiseRemoved, summary } = applySharedNoiseRemoval(sourceCleaning.cleanSourceText)
  summary.foliationMarkerCount += sourceCleaning.report.counts.foliation || 0
  summary.pageMarkerCount += sourceCleaning.report.counts.page_number_line || 0
  summary.standaloneLineNumberCount += sourceCleaning.report.counts.standalone_numeric_line || 0
  summary.standaloneLineNumberCount += sourceCleaning.report.counts.line_number_prefix || 0
  summary.footnoteAnchorCount += sourceCleaning.report.counts.footnote_anchor || 0

  const workspaceText = collapseWhitespace(
    sharedNoiseRemoved
      .replace(LEGEND_EXPLICIT_BLOCK, (_match, inner: string) => ` ${inner || ''} `)
      .replace(LEGEND_TAG_BLOCK, (_match, inner: string) => ` ${inner || ''} `)
      .replace(LEGEND_BRACKET_BLOCK, (_match, inner: string) => ` ${inner || ''} `)
      .replace(LEGEND_EXPLICIT_BOUNDARY_TAG, ' ')
      .replace(HAND_CHANGE_TAG, ' ')
      .replace(SIDE_TITLE_BLOCK, (_match, inner: string) => ` ${inner || ''} `)
      .replace(ILLUSTRATION_MARKER, ' ')
  )

  const readingText = collapseWhitespace(
    sharedNoiseRemoved
      .replace(LEGEND_EXPLICIT_BLOCK, ' ')
      .replace(LEGEND_TAG_BLOCK, ' ')
      .replace(LEGEND_BRACKET_BLOCK, ' ')
      .replace(LEGEND_EXPLICIT_BOUNDARY_TAG, ' ')
      .replace(HAND_CHANGE_TAG, ' ')
      .replace(SIDE_TITLE_BLOCK, ' ')
      .replace(ILLUSTRATION_MARKER, ' ')
  )

  const result: LedgerTextPreprocessingResult = {
    normalizedSourceText,
    cleanSourceText: sourceCleaning.cleanSourceText,
    workspaceDisplayText: workspaceText,
    readingText,
    comparisonText: analyzeTextForComparison(readingText).cleanedText,
    artifactSummary: summary,
    sourceCleaningReport: sourceCleaning.report
  }

  setCachedPreprocessingResult(cacheKey, result)
  return result
}

export const buildWitnessUnitTextIndex = (
  witness: WitnessTextSource | null | undefined,
  previousLookup?: ReadonlyMap<string, WitnessUnitTextRecord>
): Map<string, WitnessUnitTextRecord> => {
  const lookup = new Map<string, WitnessUnitTextRecord>()
  const segmentationResult = resolveActiveSegmentationResult(witness)
  const sourceText = witness?.uploaded_file?.content || ''

  if (!segmentationResult?.chunk_results?.length) {
    return lookup
  }

  segmentationResult.chunk_results.forEach(chunk => {
    ;(chunk.micro_units || []).forEach(unit => {
      const unitUuid = String(unit.unit_uuid || unit.unit_id || '').trim()
      if (!unitUuid) {
        return
      }

      const start = typeof unit.start_position === 'number' ? unit.start_position : null
      const end = typeof unit.end_position === 'number' ? unit.end_position : null
      const unitTextContent = String(unit.unit_text_content || '')
      const sourceSlice = (
        start !== null &&
        end !== null &&
        end > start &&
        end <= sourceText.length
      )
        ? sourceText.slice(start, end)
        : ''
      // Prefer the segmentation unit's own text payload. It is the canonical text
      // used by boundary editing and row-mapping snapshots, while raw source slices
      // can include neighboring bleed when start/end offsets are coarse.
      const rawText = unitTextContent || sourceSlice

      // Check the resolved text, not just witness identity: source-span fallback,
      // active-run changes, and edits to existing UUIDs must all invalidate it.
      const previous = previousLookup?.get(unitUuid)
      if (previous?.rawText === rawText) {
        lookup.set(unitUuid, previous)
        return
      }
      const preprocessed = preprocessLedgerText(rawText)
      lookup.set(unitUuid, {
        rawText,
        cleanSourceText: preprocessed.cleanSourceText,
        workspaceDisplayText: preprocessed.workspaceDisplayText,
        readingText: preprocessed.readingText,
        comparisonText: preprocessed.comparisonText
      })
    })
  })

  return lookup
}
